import { getCurrentTabId, isCurrentTabMaster } from './establishMultitabRole';

export type ClientErrorKind = 'runtime' | 'resource' | 'perf';

export type ClientErrorMetadata = Record<string, unknown> & {
  kind: ClientErrorKind;
};

type QueueEntry = {
  dedupeKey: string;
  message: string;
  stack: string;
  metadata?: ClientErrorMetadata;
};

const REPORT_INTERVAL_MS = 5_000;
const IMMEDIATE_REPORT_COOLDOWN_MS = 30_000;
const reportQueue: QueueEntry[] = [];
let reportTimer: ReturnType<typeof setTimeout> | undefined;
const immediateReportLastSentAt = new Map<string, number>();
const SENSITIVE_URL_QUERY_KEYS = [
  'ownerAccessToken',
  'loginToken',
  'token',
  'access_token',
  'refresh_token',
  'authToken',
];

type WorkerSendBeaconPayload = {
  type: 'sendBeacon';
  url: string;
  data: ArrayBuffer;
};

function getCurrentUserId() {
  if (typeof window === 'undefined') return undefined;

  const maybeWindow = window as typeof window & {
    getGlobal?: () => {
      auth?: { state?: string };
      currentUserId?: string;
    };
  };

  if (typeof maybeWindow.getGlobal !== 'function') {
    return undefined;
  }

  try {
    const global = maybeWindow.getGlobal?.();
    if (global?.auth?.state !== 'authorizationStateReady') {
      return undefined;
    }

    const userId = global.currentUserId;
    return typeof userId === 'string' && userId ? userId : undefined;
  } catch {
    return undefined;
  }
}

function sanitizeClientErrorUrl(href?: string) {
  if (!href) return href;

  try {
    const url = new URL(href);
    for (const key of SENSITIVE_URL_QUERY_KEYS) {
      if (url.searchParams.has(key)) {
        url.searchParams.set(key, '<redacted>');
      }
    }
    return url.toString();
  } catch {
    return undefined;
  }
}

function buildPayload(entry: QueueEntry) {
  return {
    message: entry.message,
    stack: entry.stack,
    url: sanitizeClientErrorUrl(self.location?.href),
    userAgent: navigator.userAgent,
    userId: getCurrentUserId(),
    appVersion: APP_VERSION,
    metadata: {
      tabId: getCurrentTabId(),
      isMasterTab: isCurrentTabMaster(),
      ...entry.metadata,
    },
  };
}

function bridgeClientErrorReport(payload: ReturnType<typeof buildPayload>) {
  const postMessageFn = (globalThis as typeof globalThis & {
    postMessage?: (message: WorkerSendBeaconPayload, options?: Transferable[]) => void;
  }).postMessage;

  if (typeof postMessageFn !== 'function') {
    return false;
  }

  const rawPayload = JSON.stringify(payload);
  const encoded = typeof TextEncoder !== 'undefined'
    ? new TextEncoder().encode(rawPayload)
    : Uint8Array.from(rawPayload, (char) => char.charCodeAt(0));
  const data = encoded.buffer.slice(
    encoded.byteOffset,
    encoded.byteOffset + encoded.byteLength,
  );

  const message: WorkerSendBeaconPayload = {
    type: 'sendBeacon',
    url: '/api/client-errors',
    data,
  };

  postMessageFn(message, [data]);
  return true;
}

function deliverClientErrorReport(payload: ReturnType<typeof buildPayload>) {
  if (typeof navigator.sendBeacon === 'function') {
    const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
    navigator.sendBeacon('/api/client-errors', blob);
    return;
  }

  bridgeClientErrorReport(payload);
}

export const __testOnly = {
  bridgeClientErrorReport,
  deliverClientErrorReport,
  sanitizeClientErrorUrl,
};

function flushClientErrorReports() {
  reportTimer = undefined;
  if (!reportQueue.length) return;

  const batch = reportQueue.splice(0, 10);
  for (const entry of batch) {
    try {
      deliverClientErrorReport(buildPayload(entry));
    } catch { /* best-effort */ }
  }

  if (reportQueue.length && !reportTimer) {
    reportTimer = setTimeout(flushClientErrorReports, REPORT_INTERVAL_MS);
  }
}

function sendClientErrorReport(entry: QueueEntry) {
  try {
    deliverClientErrorReport(buildPayload(entry));
  } catch { /* best-effort */ }
}

export function reportClientError({
  kind,
  message,
  stack = '',
  metadata,
  dedupeKey,
  mode = 'batched',
}: {
  kind: ClientErrorKind;
  message: string;
  stack?: string;
  metadata?: Record<string, unknown>;
  dedupeKey?: string;
  mode?: 'batched' | 'immediate';
}) {
  const normalizedMessage = message.slice(0, 2000);
  const normalizedStack = stack.slice(0, 8000);
  const normalizedMetadata = metadata ? { kind, ...metadata } : { kind };
  const firstFrame = normalizedStack.split('\n')[1] || '';
  const queueKey = dedupeKey || `${kind}:${normalizedMessage}:${firstFrame}`;

  if (mode === 'immediate') {
    const now = Date.now();
    const lastSentAt = immediateReportLastSentAt.get(queueKey);
    if (lastSentAt !== undefined && now - lastSentAt < IMMEDIATE_REPORT_COOLDOWN_MS) {
      return;
    }

    immediateReportLastSentAt.set(queueKey, now);
    sendClientErrorReport({
      dedupeKey: queueKey,
      message: normalizedMessage,
      stack: normalizedStack,
      metadata: normalizedMetadata,
    });
    return;
  }

  if (reportQueue.some((entry) => entry.dedupeKey === queueKey)) {
    return;
  }

  reportQueue.push({
    dedupeKey: queueKey,
    message: normalizedMessage,
    stack: normalizedStack,
    metadata: normalizedMetadata,
  });

  if (!reportTimer) {
    reportTimer = setTimeout(flushClientErrorReports, REPORT_INTERVAL_MS);
  }
}
